import React, { useEffect, useState, useCallback } from "react";
import { useNotify } from "react-admin";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

// Single control for which assumptions the project auto-applies on recalc.
// GET/PUT /projects/:id/assumption-optin. A field checked here → the completeness job materialises
// its assumption when recalculating; unchecked → the gap stays pending. Starts empty per project.

const useStyles = makeStyles((theme) => ({
  head: { display: "flex", alignItems: "center", gap: 8, marginBottom: theme.spacing(1) },
  title: { fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" },
  hint: { fontSize: 11, color: theme.palette.text.secondary },
  list: {
    maxHeight: 240,
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(0.5, 1),
  },
  row: { display: "flex", alignItems: "center", gap: 6, padding: "2px 0", fontSize: 13 },
  grow: { flex: "1 1 auto", minWidth: 0 },
  bulk: { display: "flex", gap: 8, margin: theme.spacing(1, 0) },
  count: { fontSize: 12, color: theme.palette.text.secondary, marginLeft: "auto" },
}));

const AssumptionOptinSelector = ({ api, projectId }) => {
  const classes = useStyles();
  const notify = useNotify();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const r = await api("GET", `/projects/${projectId}/assumption-optin`);
    setLoading(false);
    setFields(r.ok ? r.data.fields ?? [] : []);
    setDirty(false);
  }, [api, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) => {
    setFields((fs) => fs.map((f) => (f.fieldDefinitionId === id ? { ...f, enabled: !f.enabled } : f)));
    setDirty(true);
  };
  const setAll = (val) => {
    setFields((fs) => fs.map((f) => ({ ...f, enabled: val })));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    const items = fields.map((f) => ({ fieldDefinitionId: f.fieldDefinitionId, enabled: f.enabled }));
    const r = await api("PUT", `/projects/${projectId}/assumption-optin`, { items });
    setSaving(false);
    if (r.ok) {
      notify("Selección guardada — recalcula para aplicarla", "success");
      setFields(r.data.fields ?? fields);
      setDirty(false);
    } else {
      notify(`Error ${r.status} al guardar`, "error");
    }
  };

  const enabledCount = fields.filter((f) => f.enabled).length;

  return (
    <Box>
      <div className={classes.head}>
        <span className={classes.title} style={{ textTransform: "none", fontSize: 15 }}>
          Valores que Bcome puede completar automáticamente
        </span>
        <span className={classes.count}>
          {enabledCount} de {fields.length} campos activados
        </span>
      </div>
      <Typography className={classes.hint} gutterBottom>
        Al recalcular, Bcome completará automáticamente los campos seleccionados cuando falte
        información. Los demás quedarán pendientes de revisión.
      </Typography>

      <div className={classes.bulk}>
        <Button size="small" variant="outlined" onClick={() => setAll(true)} disabled={loading}>
          Todas
        </Button>
        <Button size="small" variant="outlined" onClick={() => setAll(false)} disabled={loading}>
          Ninguna
        </Button>
        {dirty ? (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={save}
            disabled={saving}
            style={{ marginLeft: "auto" }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : "Guardar selección"}
          </Button>
        ) : (
          <span style={{ marginLeft: "auto", color: "#059669", fontSize: 13, fontWeight: 600 }}>
            ✓ Cambios guardados
          </span>
        )}
      </div>

      {loading ? (
        <Box textAlign="center" py={2}>
          <CircularProgress size={22} />
        </Box>
      ) : fields.length === 0 ? (
        <Typography className={classes.hint}>No hay campos asumibles.</Typography>
      ) : (
        <div className={classes.list}>
          {fields.map((f) => (
            <div key={f.fieldDefinitionId} className={classes.row}>
              <Checkbox
                size="small"
                checked={!!f.enabled}
                onChange={() => toggle(f.fieldDefinitionId)}
                color="primary"
                style={{ padding: 2 }}
              />
              <span className={classes.grow}>{f.displayName}</span>
              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{f.level}</span>
            </div>
          ))}
        </div>
      )}
      <Divider style={{ marginTop: 12 }} />
    </Box>
  );
};

export default AssumptionOptinSelector;
